import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import LogoWordmark from "./Logo";

const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "Pricing", href: "#pricing" },
  { label: "Gallery", href: "#gallery" },
  { label: "Reviews", href: "#reviews" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (href) => {
    setOpen(false);
    if (!onHome) {
      navigate("/" + href);
      return;
    }
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass border-b border-white/5" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="nav-logo-link" onClick={() => setOpen(false)}>
          <LogoWordmark />
        </Link>

        <div className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <button
              key={l.label}
              onClick={() => handleNavClick(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm font-medium text-[#F5F1E8]/80 hover:text-[#7CB342] transition-colors"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <a href="tel:+14048589811" className="text-sm font-medium text-[#F5F1E8]/70 hover:text-[#7CB342]" data-testid="nav-phone">
            404-858-9811
          </a>
          <Link
            to="/book"
            data-testid="nav-book-now-btn"
            className="btn-pickle inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm"
          >
            Book Now
          </Link>
        </div>

        <button
          className="lg:hidden p-2 text-[#F5F1E8]"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden glass border-t border-white/5" data-testid="mobile-menu">
          <div className="px-4 py-6 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.label}
                onClick={() => handleNavClick(l.href)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className="text-left px-3 py-3 rounded-lg text-base font-medium text-[#F5F1E8]/85 hover:bg-white/5 hover:text-[#7CB342] transition"
              >
                {l.label}
              </button>
            ))}
            <Link
              to="/book"
              onClick={() => setOpen(false)}
              data-testid="mobile-book-now-btn"
              className="btn-pickle mt-3 inline-flex items-center justify-center rounded-full px-5 py-3 text-sm"
            >
              Book Now
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
